import { useMemo } from "react";
import {
  FormattedMessage,
  defineMessages,
  useIntl,
  type MessageDescriptor,
} from "react-intl";

import { DismissRegular } from "@fluentui/react-icons";
import { Badge, Button } from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";
import type { RecallStatus as Status } from "@aglocal/schema/Recall";

import { useMissionControl } from "@aglocal/web/mission-control";
import titleCase from "@aglocal/web/helpers/titleCase";
import useSafeState from "@aglocal/web/hooks/useSafeState";

import Callout, { CalloutProps } from "@aglocal/web/components/Callout";
import { Span } from "@aglocal/web/components/Typography";
import Stack from "@aglocal/web/components/Stack";

const STATUS_LABELS = defineMessages({
  ready: {
    defaultMessage: "Ready",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  joining_call: {
    defaultMessage: "Joining Call",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  in_waiting_room: {
    defaultMessage: "In Waiting Room",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  participant_in_waiting_room: {
    defaultMessage: "Participant In Waiting Room",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  in_call_not_recording: {
    defaultMessage: "In Call Not Recording",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  recording_permission_allowed: {
    defaultMessage: "Recording Permission Allowed",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  recording_permission_denied: {
    defaultMessage: "Recording Permission Denied",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  in_call_recording: {
    defaultMessage: "In Call Recording",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  call_ended: {
    defaultMessage: "Call Ended",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  recording_done: {
    defaultMessage: "Recording Done",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  done: {
    defaultMessage: "Done",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  fatal: {
    defaultMessage: "Fatal",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  analysis_done: {
    defaultMessage: "Analysis Done",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  analysis_failed: {
    defaultMessage: "Analysis Failed",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
  media_expired: {
    defaultMessage: "Media Expired",
    description:
      "Status of the Talent Assistant recording bot, shown next to a colored badge in the in-meeting sidebar.",
  },
});

const messages = defineMessages({
  status: {
    defaultMessage: "Recall Status: {status}",
    description:
      "Line in the in-meeting sidebar showing the state of the Talent Assistant recording bot; {status} is the state.",
  },
  unknownStatus: {
    defaultMessage: "Unknown",
    description:
      "Status of the Talent Assistant recording bot when it has not reported any state yet.",
  },
  refusedRecording: {
    defaultMessage: "Talent Refused Recording",
    description:
      "Status of the Talent Assistant recording bot when the talent declined to be recorded.",
  },
  started: {
    defaultMessage: "Talent Assistant started",
    description:
      "Confirmation shown after the recruiter starts the Talent Assistant recording bot.",
  },
  startFailed: {
    defaultMessage: "Failed to start Talent Assistant",
    description:
      "Error shown when the Talent Assistant recording bot could not be started.",
  },
  stopped: {
    defaultMessage: "Talent Assistant stopped",
    description:
      "Confirmation shown after the recruiter stops the Talent Assistant recording bot.",
  },
  stopFailed: {
    defaultMessage: "Failed to stop Talent Assistant",
    description:
      "Error shown when the Talent Assistant recording bot could not be stopped.",
  },
  dismiss: {
    defaultMessage: "dismiss",
    description:
      "Accessible label of the button that closes the Talent Assistant start/stop confirmation.",
  },
});

function getBadgeColor(status: Status | undefined) {
  switch (status) {
    case "in_call_recording":
      return "success";
    case "joining_call":
    case "participant_in_waiting_room":
    case "in_call_not_recording":
    case "recording_permission_allowed":
      return "warning";
    case "in_waiting_room":
    case "recording_permission_denied":
    case "fatal":
      return "danger";
    default:
      return "informative";
  }
}

function getStatusLabel(status: string): MessageDescriptor | undefined {
  return Object.hasOwn(STATUS_LABELS, status)
    ? STATUS_LABELS[status as keyof typeof STATUS_LABELS]
    : undefined;
}

function isBotRunning(status: Status | undefined) {
  return !["done", "fatal", "call_ended", "ready", "", undefined].includes(
    status,
  );
}

export interface RecallStatusProps {
  interview: Interview;
}

export default function RecallStatus({
  interview: { id, recall, joinWebUrl, interviewee },
}: RecallStatusProps) {
  const { $t } = useIntl();
  const api = useMissionControl();
  const [loading, setLoading] = useSafeState(false);
  const [callout, setCallout] = useSafeState<CalloutProps | null>(null);
  const allowRecording: boolean = interviewee?.recordOptOut !== true;
  const isRunning = useMemo(
    () => isBotRunning(recall?.status),
    [recall?.status],
  );

  const formatStatus = (status: Status | undefined) => {
    if (!allowRecording) {
      return $t(messages.refusedRecording);
    }
    if (status == null || status === "%UNKNOWN%") {
      return $t(messages.unknownStatus);
    }
    const label = getStatusLabel(status);
    return label ? $t(label) : titleCase(status.replace(/_/g, " "));
  };

  const startBot = async (teamsMeetingUrl: string) => {
    setLoading(true);
    try {
      await api.startMeetingBot({
        iId: id,
        teamsMeetingUrl,
      });
      setCallout({
        title: $t(messages.started),
        intent: "success",
      });
    } catch (error) {
      setCallout({
        title: $t(messages.startFailed),
        intent: "error",
      });
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const stopBot = async (botId: string) => {
    setLoading(true);
    try {
      await api.stopMeetingBot({ iId: id, botId });
      setCallout({
        title: $t(messages.stopped),
        intent: "info",
      });
    } catch (error) {
      setCallout({
        title: $t(messages.stopFailed),
        intent: "error",
      });
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap="s">
      {allowRecording && (
        <Stack horizontal verticalAlign="center" gap="s">
          <Badge color={getBadgeColor(recall?.status)} />
          <Span>
            {$t(messages.status, { status: formatStatus(recall?.status) })}
          </Span>
        </Stack>
      )}
      {callout && (
        <Callout
          containerAction={
            <Button
              onClick={() => {
                setCallout(null);
              }}
              aria-label={$t(messages.dismiss)}
              appearance="transparent"
              icon={<DismissRegular />}
            />
          }
          {...callout}
        />
      )}
      {!isRunning && recall?.status === "ready" && joinWebUrl && (
        <Button disabled={loading} onClick={() => void startBot(joinWebUrl)}>
          <FormattedMessage
            defaultMessage="Start Talent Assistant"
            description="Button that starts the Talent Assistant recording bot in the meeting."
          />
        </Button>
      )}
      {isRunning && recall?.botId && (
        <Button
          disabled={loading}
          size="small"
          onClick={() => void stopBot(recall.botId)}
        >
          <FormattedMessage
            defaultMessage="Stop Talent Assistant"
            description="Button that stops the Talent Assistant recording bot in the meeting."
          />
        </Button>
      )}
    </Stack>
  );
}
